/** 12 Week Year client — Daniel 2026-07-05. */
import { apiFetch } from '../auth/api';
import { getToken } from '../auth/tokenStore';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export interface TwyCycle {
  id: number;
  label: string;
  start_date: number;
  weeks: number;
  current_week: number;
}

export interface TwyGoal {
  id: number;
  position: number;
  title: string;
  target_count: number | null;
  tally_done: number;
  tactics_total?: number;
  tactics_done?: number;
  /** Build 94: set when the goal counts itself from the deals. */
  auto?: TwyAutoMetric | null;
  auto_window?: { from: string; to: string } | null;
}

export type TwyAutoMetric = 'closings' | 'pendings' | 'listings';
export const AUTO_METRIC_LABELS: Record<TwyAutoMetric, string> = {
  closings: 'closed deals',
  pendings: 'contracts written',
  listings: 'listings taken',
};

export interface TwyTactic {
  id: number;
  goal_id: number;
  title: string;
  due_week: number | null;
  status: 'open' | 'done';
  // L-99b (Daniel 2026-07-08): countable tactics. target=1 = boolean.
  target_count: number;
  done_count: number;
  // L-100 (Daniel 2026-07-08): sort_order for up/down reorder.
  position: number;
}

export interface TwyFundamental {
  id: number;
  title: string;
}

export interface TwyMember {
  user_id: number;
  name: string;
  /** Build 25: null when the person is on the roster but has not started a
   *  plan for this cycle. The card renders as "not started" rather than the
   *  person being missing from the board. */
  plan_id: number | null;
  /** Build 25: false for Angel, Grace and Rachelle. Their cards carry goals
   *  and tactics but never production targets. */
  is_producing?: boolean;
  goals: TwyGoal[];
  last_score: number | null;
  last_score_week: number | null;
  this_week_started: boolean;
  this_week_submitted: boolean;
}

export interface TwyLastCycle {
  id: number;
  label: string;
  start_date: number;
  weeks: number;
  ended_at: number | null;
  ended_by: string | null;
}

export function getTwyCycle(): Promise<{ cycle: TwyCycle | null; members: TwyMember[]; last_cycle?: TwyLastCycle | null }> {
  return apiFetch('/api/twy/cycle');
}

/** Build 93: close the active cycle now. */
export function endTwyCycle(id: number): Promise<{ ok: true }> {
  return apiFetch(`/api/twy/cycle/${id}/end`, { method: 'POST' });
}

export function startTwyCycle(body: { label: string; start_date: number; weeks?: number }): Promise<{ ok: true }> {
  return apiFetch('/api/twy/cycle', { method: 'POST', body: JSON.stringify(body) });
}

export function ensureMyPlan(): Promise<{ ok: true; plan_id: number }> {
  return apiFetch('/api/twy/plans', { method: 'POST' });
}

export function getTwyPlan(userId: number): Promise<{
  cycle: TwyCycle;
  plan: { id: number; user_id: number; name: string; can_edit: boolean };
  goals: TwyGoal[];
  tactics: TwyTactic[];
  fundamentals: TwyFundamental[];
}> {
  return apiFetch(`/api/twy/plans/${userId}`);
}

export function addGoal(body: { plan_id: number; title: string; target_count?: number | null; auto_metric?: TwyAutoMetric | null }) {
  return apiFetch('/api/twy/goals', { method: 'POST', body: JSON.stringify(body) });
}
export function patchGoal(id: number, body: { title?: string; target_count?: number | null; tally_done?: number; auto_metric?: TwyAutoMetric | null }) {
  return apiFetch(`/api/twy/goals/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}
export function deleteGoal(id: number) {
  return apiFetch(`/api/twy/goals/${id}`, { method: 'DELETE' });
}
export function addTactic(goalId: number, body: { title: string; due_week?: number | null; target_count?: number }) {
  return apiFetch(`/api/twy/goals/${goalId}/tactics`, { method: 'POST', body: JSON.stringify(body) });
}
export function patchTactic(id: number, body: { title?: string; due_week?: number | null; status?: 'open' | 'done'; target_count?: number; done_count?: number; position?: number }) {
  return apiFetch(`/api/twy/tactics/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}
export function deleteTactic(id: number) {
  return apiFetch(`/api/twy/tactics/${id}`, { method: 'DELETE' });
}
// L-100 fix (Daniel 2026-07-20): server-side reorder. The old "swap two
// positions" client call never moved anything because every tactic shared
// the default position. This renumbers the whole sibling list server-side.
export function moveTactic(id: number, dir: 'up' | 'down') {
  return apiFetch(`/api/twy/tactics/${id}/move`, { method: 'POST', body: JSON.stringify({ dir }) });
}
export function addFundamental(body: { plan_id: number; title: string }) {
  return apiFetch('/api/twy/fundamentals', { method: 'POST', body: JSON.stringify(body) });
}
export function deleteFundamental(id: number) {
  return apiFetch(`/api/twy/fundamentals/${id}`, { method: 'DELETE' });
}

export interface TwyScorecard {
  id: number;
  week_no: number;
  mit: string | null;
  mit_done: number;
  ranking: number | null;
  listings_taken: number;
  auction_listings: number;
  buyers_uc: number;
  fundamentals_json: Record<string, number>;
  submitted_at: number | null;
}

export interface TwyScorecardTask {
  id: number;
  title: string;
  due_day: string | null;
  owner: string | null;
  done: number;
  // L-99 (Daniel 2026-07-08): countable tasks. target=1 = classic
  // boolean checkbox. target>1 renders as "N of M done" with +/-.
  target_count: number;
  done_count: number;
  // L-100 (Daniel 2026-07-08): sort_order for up/down reorder.
  position: number;
}

export function getScorecard(userId: number, week?: number): Promise<{
  cycle: TwyCycle;
  week_no: number;
  can_edit: boolean;
  scorecard: TwyScorecard;
  tasks: TwyScorecardTask[];
  fundamentals: TwyFundamental[];
  goals: Array<{ position: number; title: string }>;
  /** Build 94: this week's numbers, live from the deals. */
  live?: { closings: number; pendings: number; listings: number; auctions: number; buyers_uc: number; from: string; to: string };
}> {
  const qs = new URLSearchParams({ user_id: String(userId) });
  if (week) qs.set('week', String(week));
  return apiFetch(`/api/twy/scorecards?${qs}`);
}

export function patchScorecard(
  id: number,
  body: Partial<{
    mit: string | null;
    mit_done: boolean;
    ranking: number | null;
    listings_taken: number;
    auction_listings: number;
    buyers_uc: number;
    fundamentals: Record<string, number>;
    submit: boolean;
  }>,
) {
  return apiFetch(`/api/twy/scorecards/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export function addScorecardTask(scorecardId: number, body: { title: string; due_day?: string | null; owner?: string | null; target_count?: number }) {
  return apiFetch(`/api/twy/scorecards/${scorecardId}/tasks`, { method: 'POST', body: JSON.stringify(body) });
}
export function patchScorecardTask(id: number, body: { title?: string; due_day?: string | null; owner?: string | null; done?: boolean; target_count?: number; done_count?: number; position?: number }) {
  return apiFetch(`/api/twy/scorecard-tasks/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}
export function deleteScorecardTask(id: number) {
  return apiFetch(`/api/twy/scorecard-tasks/${id}`, { method: 'DELETE' });
}
// L-100 fix (Daniel 2026-07-20): server-side reorder for weekly tasks.
export function moveScorecardTask(id: number, dir: 'up' | 'down') {
  return apiFetch(`/api/twy/scorecard-tasks/${id}/move`, { method: 'POST', body: JSON.stringify({ dir }) });
}

export interface TwyCloseWeekResult {
  submitted_week: number;
  next_week_no: number | null;
  next_scorecard_id: number | null;
  carried: number;
}

// Daniel 2026-07-20: finalize a week and carry the chosen unfinished
// tasks into the next week. Powers the Monday close-out banner.
export function closeWeek(
  scorecardId: number,
  carryTaskIds: number[],
  doneTaskIds: number[] = [],
): Promise<TwyCloseWeekResult> {
  return apiFetch(`/api/twy/scorecards/${scorecardId}/close-week`, {
    method: 'POST',
    body: JSON.stringify({ carry_task_ids: carryTaskIds, done_task_ids: doneTaskIds }),
  });
}

/** Fetch + blob so the auth header rides along, then open. */
export async function openTwyPdf(path: string): Promise<void> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`PDF failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
